/**
 * API авторизации и управления сессией.
 * Эндпоинты из qhub-gateway.
 */

import { normalizeCapabilities } from '@/auth/permissions';
import { LocalizedError } from '@/utils/displayMessage';
import { ApiError, api, setToken } from './client';
import type {
	AcceptInviteParams,
	ChangePasswordParams,
	CheckEmailResponse,
	InvitationInfo,
	RestorePasswordParams,
	SessionResponse,
	SignUpAwsParams,
	SignUpParams,
	SSOLoginResponse,
	User,
	WorkspaceRole,
} from './types';

const AUTH_BASE = '/v1';

/** Вызовы экранов без сессии: токен прежней сессии с ними не отправляется */
const GUEST = { anonymous: true } as const;

function guestPost<T>(url: string, body: unknown): Promise<T> {
	return api.request<T>(url, { ...GUEST, method: 'POST', body });
}

/** Прежние значения роли в ответах и интерфейсе: хранимое user и старое member — это analyst */
const ROLE_ALIASES: Record<string, WorkspaceRole> = {
	admin: 'admin',
	analyst: 'analyst',
	viewer: 'viewer',
	user: 'analyst',
	member: 'analyst',
};

function sessionCompanyId(raw: unknown): string | null {
	if (raw === undefined || raw === null || raw === '') {
		return null;
	}
	if (typeof raw === 'string') {
		return raw;
	}
	/* Явный адаптер прежнего числового значения: только безопасное целое */
	if (typeof raw === 'number' && Number.isSafeInteger(raw)) {
		return String(raw);
	}
	throw new LocalizedError({ messageKey: 'auth:errors.sessionInvalid' });
}

/**
 * Клиент сессии из ответа сервера (signin, session, accept-invite, restore-password).
 * company_id приводится к строке без потери точности и не подменяется нулём или прежней
 * компанией; права берутся только из ответа сервера и из роли не выводятся.
 */
export function normalizeSessionClient(raw: unknown): User {
	if (raw === null || typeof raw !== 'object') {
		throw new LocalizedError({ messageKey: 'auth:errors.sessionInvalid' });
	}
	const source = raw as Record<string, unknown>;
	if (typeof source.id !== 'number' || typeof source.email !== 'string') {
		throw new LocalizedError({ messageKey: 'auth:errors.sessionInvalid' });
	}
	return {
		id: source.id,
		email: source.email,
		full_name: typeof source.full_name === 'string' ? source.full_name : null,
		job_title: typeof source.job_title === 'string' ? source.job_title : null,
		skip_intro: source.skip_intro === true,
		user_hash: typeof source.user_hash === 'string' ? source.user_hash : null,
		company_id: sessionCompanyId(source.company_id),
		company_name: typeof source.company_name === 'string' ? source.company_name : null,
		company_timezone: typeof source.company_timezone === 'string' ? source.company_timezone : null,
		role: typeof source.role === 'string' ? (ROLE_ALIASES[source.role] ?? null) : null,
		capabilities: normalizeCapabilities(source.capabilities),
	};
}

function normalizeSession(raw: unknown): SessionResponse {
	const client =
		raw !== null && typeof raw === 'object' ? (raw as { client?: unknown }).client : undefined;
	return { client: normalizeSessionClient(client) };
}

/**
 * Вход по email + пароль.
 * Token приходит в заголовке 'Token', user — в теле ответа.
 * Используем fetch напрямую (не api client) для доступа к заголовкам.
 */
export async function signIn(
	login: string,
	password: string,
): Promise<{ user: User; token: string }> {
	const response = await fetch(`/api${AUTH_BASE}/signin`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		credentials: 'include',
		body: JSON.stringify({ login, password }),
	});

	if (!response.ok) {
		/* Код и статус ответа -- странице входа: она называет причину словами интерфейса,
		   текст сервера и statusText пользователю не показываются */
		const errorData: unknown = await response.json().catch(() => null);
		const message = (errorData as { message?: unknown } | null)?.message;
		throw new ApiError(
			response.status,
			typeof message === 'string' && message !== '' ? message : response.statusText,
			errorData,
		);
	}

	const token = response.headers.get('Token');
	if (!token) {
		throw new LocalizedError({ messageKey: 'auth:errors.tokenMissing' });
	}

	setToken(token);
	const data = normalizeSession(await response.json());
	return { user: data.client, token };
}

/**
 * Регистрация. Без подтверждения email сервер сразу открывает сессию (токен в заголовке
 * Token сохраняет клиент API); с подтверждением отвечает 200 без тела — null: сессии нет,
 * пользователь подтверждает адрес по письму.
 */
export async function signUp(params: SignUpParams): Promise<SessionResponse | null> {
	const raw = await guestPost<unknown>(`${AUTH_BASE}/signup`, params);
	return raw === undefined || raw === null ? null : normalizeSession(raw);
}

/** Подтверждение email по секрету из письма: сервер активирует учётную запись и открывает сессию */
export async function verifyEmail(secret: string): Promise<SessionResponse> {
	return normalizeSession(await guestPost<unknown>(`${AUTH_BASE}/client/verification`, { secret }));
}

/** Повторное письмо подтверждения; сервер ограничивает частоту (429 с Retry-After) */
export async function resendVerification(email: string): Promise<void> {
	await guestPost<unknown>(`${AUTH_BASE}/client/verification/resend`, { email });
}

/** Кто пригласил: имя (created_by_name), а без него — email пригласившего (created_by) */
function inviterOf(raw: Record<string, unknown>): string | null {
	if (typeof raw.created_by_name === 'string' && raw.created_by_name !== '') {
		return raw.created_by_name;
	}
	return typeof raw.created_by === 'string' && raw.created_by !== '' ? raw.created_by : null;
}

/** Приглашение по токену из ссылки; просроченное или использованное — 404 */
export async function getInvitationInfo(token: string): Promise<InvitationInfo> {
	const raw = await api.request<Record<string, unknown>>(`${AUTH_BASE}/clients/token-info`, {
		...GUEST,
		method: 'GET',
		params: { token },
	});
	return {
		email: String(raw.email ?? ''),
		full_name: typeof raw.full_name === 'string' && raw.full_name !== '' ? raw.full_name : null,
		invited_by: inviterOf(raw),
		sso: raw.is_sso_enabled === true,
		company_name: typeof raw.company_name === 'string' ? raw.company_name : null,
		role: typeof raw.role === 'string' ? (ROLE_ALIASES[raw.role] ?? null) : null,
	};
}

/** Принятие приглашения (accept-invite / accept-enrollment) */
export async function acceptInvite(params: AcceptInviteParams): Promise<SessionResponse> {
	return normalizeSession(await guestPost<unknown>(`${AUTH_BASE}/clients/accept-invite`, params));
}

/** Получение текущей сессии (проверка авторизации) */
export async function getSession(): Promise<SessionResponse> {
	return normalizeSession(await api.get<unknown>(`${AUTH_BASE}/session`));
}

/** Выход из системы */
export async function signOut(): Promise<void> {
	await api.post<void>(`${AUTH_BASE}/signout`);
}

/** Проверка email (SSO/AWS статус) */
export async function checkEmail(email: string): Promise<CheckEmailResponse> {
	return guestPost<CheckEmailResponse>(`${AUTH_BASE}/clients/email-check`, { email });
}

/** Получение URL для SSO входа */
export async function getSSOLoginUrl(email: string): Promise<SSOLoginResponse> {
	return guestPost<SSOLoginResponse>(`${AUTH_BASE}/sso/login-url`, { email });
}

/** Запрос email для сброса пароля */
export async function restorePasswordRequest(email: string): Promise<void> {
	await guestPost<void>(`${AUTH_BASE}/clients/restore-password-request`, { email });
}

/** Сброс пароля по токену */
export async function restorePassword(params: RestorePasswordParams): Promise<SessionResponse> {
	return normalizeSession(
		await guestPost<unknown>(`${AUTH_BASE}/clients/restore-password`, params),
	);
}

/** Смена пароля авторизованным пользователем */
export async function changePassword(params: ChangePasswordParams): Promise<void> {
	await api.put<void>(`${AUTH_BASE}/client/change-password`, params);
}

/** Регистрация через AWS Marketplace */
export async function signUpAws(params: SignUpAwsParams): Promise<SessionResponse | null> {
	const raw = await guestPost<unknown>(`${AUTH_BASE}/signup-aws`, params);
	return raw === undefined || raw === null ? null : normalizeSession(raw);
}

/**
 * Привязка существующей учётной записи к подписке AWS Marketplace: сервер проверяет
 * пароль и подписку и открывает сессию (токен в заголовке Token сохраняет клиент API).
 */
export async function connectAws(params: SignUpAwsParams): Promise<SessionResponse | null> {
	const raw = await guestPost<unknown>(`${AUTH_BASE}/connect-aws`, params);
	return raw === undefined || raw === null ? null : normalizeSession(raw);
}
